import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../authToken';
import { API_BASE_URL } from '../config';
import DriveInfoAddPanel from './DriveInfoAddPanel';
import DriveInfoEditPanel from './DriveInfoEditPanel';
import DriveInfoItemMenuPanel from './DriveInfoItemMenuPanel';

const DRIVE_INFO_ENDPOINT = `${API_BASE_URL}/docentry`; 
const KEY_BASE = "infrastructure.drive";

function normalize(value) {
  return String(value ?? '').trim();
}

function resolveParam1(item) {
  return item?.param1 ?? item?.purpose ?? '';
}

function resolveParam2(item) {
  return item?.param2 ?? item?.flag ?? '';
}

function buildPayload({ name, param2, value, param1 }, useLegacyNames = false) {
  if (useLegacyNames) {
    return { path: KEY_BASE, name, flag: param2, value, purpose: param1 };
  }
  return { path: KEY_BASE, name, param2, value, param1 };
}

export default function DriveInfoListPanel({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [menuItemId, setMenuItemId] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const fetchItems = () => {
    setLoading(true);
    fetch(DRIVE_INFO_ENDPOINT + "/by-path/" + KEY_BASE, {
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch drive info');
        return res.json();
      })
      .then(data => {
        const normalizedItems = Array.isArray(data)
          ? data.map(item => ({
              ...item,
              param1: resolveParam1(item),
              param2: resolveParam2(item)
            }))
          : [];
        setItems(normalizedItems);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const an = normalize(a?.name);
      const bn = normalize(b?.name);
      const byName = an.localeCompare(bn, 'pl', { sensitivity: 'base', numeric: true });
      if (byName !== 0) return byName;

      const byFlag = normalize(a?.param2).localeCompare(normalize(b?.param2), 'pl', { sensitivity: 'base', numeric: true });
      if (byFlag !== 0) return byFlag;

      const byValue = normalize(a?.value).localeCompare(normalize(b?.value), 'pl', { sensitivity: 'base', numeric: true });
      if (byValue !== 0) return byValue;

      return normalize(a?.param1).localeCompare(normalize(b?.param1), 'pl', { sensitivity: 'base', numeric: true });
    });
  }, [items]);

  const handleAdd = ({ name, param2, value, param1 }) => {
    setActionError(null);
    fetch(DRIVE_INFO_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify(buildPayload({ name, param2, value, param1 }, false))
    })
      .then(res => {
        if (res.ok) return res.json();
        return fetch(DRIVE_INFO_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': authToken } : {})
          },
          body: JSON.stringify(buildPayload({ name, param2, value, param1 }, true))
        }).then(legacyRes => {
          if (!legacyRes.ok) throw new Error('Failed to add drive info');
          return legacyRes.json();
        });
      })
      .then(() => {
        setShowAddPanel(false);
        fetchItems();
      })
      .catch(err => setActionError(err.message));
  };

  const handleEdit = (updatedItem) => {
    setActionError(null);
    if (!updatedItem?.id) {
      setActionError('Missing item ID');
      return;
    }

    const payloadData = {
      name: updatedItem.name,
      param2: resolveParam2(updatedItem),
      value: updatedItem.value,
      param1: resolveParam1(updatedItem)
    };

    fetch(`${DRIVE_INFO_ENDPOINT}/${updatedItem.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {})
      },
      body: JSON.stringify(buildPayload(payloadData, false))
    })
      .then(res => {
        if (res.ok) return res.json();
        return fetch(`${DRIVE_INFO_ENDPOINT}/${updatedItem.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': authToken } : {})
          },
          body: JSON.stringify(buildPayload(payloadData, true))
        }).then(legacyRes => {
          if (!legacyRes.ok) throw new Error('Failed to save changes');
          return legacyRes.json();
        });
      })
      .then(() => {
        setEditItem(null);
        fetchItems();
      })
      .catch(err => setActionError(err.message));
  };

  const handleDelete = (item) => {
    if (!item?.id) {
      setError('Brak ID wpisu');
      setConfirmDeleteItem(null);
      return;
    }

    fetch(`${DRIVE_INFO_ENDPOINT}/${item.id}`, {
      method: 'DELETE',
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {})
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to delete');
        setConfirmDeleteItem(null);
        fetchItems();
      })
      .catch(err => {
        setError(err.message);
        setConfirmDeleteItem(null);
      });
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '860px',
      maxWidth: '98vw',
      minHeight: 0,
      height: '620px',
      maxHeight: '96vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >✖️</button>

      <h2 style={{ marginBottom: 16 }}>Drive info</h2>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: '1px solid #09f',
            background: '#eef8ff',
            color: '#036',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onClick={() => {
            setActionError(null);
            setShowAddPanel(true);
          }}
        >➕ Dodaj</button>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ background: '#f8f8f8' }}>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, width: '24%' }}>Nazwa</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, width: '8%' }}>Flaga</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, width: '31%' }}>Wartość</th>
            <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, width: '31%' }}>Cel</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item, idx) => {
            const itemKey = String(item?.id ?? `${normalize(item?.name)}|${normalize(item?.param2)}|${normalize(item?.value)}|${normalize(item?.param1)}|${idx}`);
            return (
              <tr key={itemKey} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '8px', width: '24%', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item?.name}</td>
                <td style={{ padding: '8px', width: '8%', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item?.param2}</td>
                <td style={{ padding: '8px', width: '31%', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item?.value}</td>
                <td style={{ padding: '8px', width: '31%', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item?.param1}</td>
                <td style={{ padding: '8px', textAlign: 'center', position: 'relative' }}>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: 22,
                      cursor: 'pointer',
                      color: '#888',
                      marginLeft: 8
                    }}
                    aria-label="Menu"
                    onClick={e => {
                      e.stopPropagation();
                      setMenuItemId(itemKey);
                    }}
                  >⋯</button>

                  {menuItemId === itemKey && (
                    <DriveInfoItemMenuPanel
                      onEdit={() => {
                        setMenuItemId(null);
                        setActionError(null);
                        setEditItem(item);
                      }}
                      onDelete={() => {
                        setMenuItemId(null);
                        setConfirmDeleteItem(item);
                      }}
                      onCancel={() => setMenuItemId(null)}
                      style={{ right: 24, top: 40 }}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {!loading && items.length === 0 && <div>No drive info entries.</div>}

      {confirmDeleteItem && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
          padding: 28,
          zIndex: 13000,
          minWidth: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16
        }}>
          <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>
            Remove drive <b>{confirmDeleteItem?.name}</b>?
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <button
              onClick={() => handleDelete(confirmDeleteItem)}
              style={{
                padding: '10px 24px',
                fontSize: '1.05rem',
                fontWeight: 600,
                borderRadius: 8,
                border: '1px solid #f66',
                background: '#fff0f0',
                color: '#a22',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
            >Remove</button>
            <button
              onClick={() => setConfirmDeleteItem(null)}
              style={{
                padding: '10px 24px',
                fontSize: '1.05rem',
                fontWeight: 600,
                borderRadius: 8,
                border: '1px solid #bbb',
                background: '#f8f8f8',
                color: '#555',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
            >Cancel</button>
          </div>
        </div>
      )}

      {showAddPanel && (
        <DriveInfoAddPanel
          onAdd={handleAdd}
          onCancel={() => {
            setShowAddPanel(false);
            setActionError(null);
          }}
          error={actionError}
        />
      )}

      {editItem && (
        <DriveInfoEditPanel
          item={editItem}
          onSave={handleEdit}
          onCancel={() => {
            setEditItem(null);
            setActionError(null);
          }}
          error={actionError}
        />
      )}
    </div>
  );
}
